import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { AiAdminService } from '../../services/ai-admin.service';
import { HttpService } from '../../services/http.service';

const TRIGGER_CONFIG: Record<string, { label: string; color: string }> = {
  face_not_matched: { label: 'Face Not Matched', color: '#DBBE00' },
  no_face_detected: { label: 'No Face Detected', color: '#D17E3F' },
  login_rejected: { label: 'Log In Rejected', color: '#E6393E' },
};

const WARNING_SVG = (color: string) =>
  `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="display:inline;vertical-align:middle;margin-right:4px" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L1 21h22L12 2z" fill="${color}" opacity="0.15"/><path d="M12 2L1 21h22L12 2z" stroke="${color}" stroke-width="2" stroke-linejoin="round"/><line x1="12" y1="10" x2="12" y2="14" stroke="${color}" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="17.5" r="1" fill="${color}"/></svg>`;

@Component({
  selector: 'app-ai-logs-list-page',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridListComponent, HeaderComponent, SelectInputComponent, ListActionButtonsComponent],
  templateUrl: './ai-logs-list-page.component.html',
})
export class AiLogsListPageComponent implements OnInit {

  page_name = 'AI Logs';
  params: any = {};
  items: any = { data: [], count: 0 };

  university_options: any[] = [];
  exam_options: any[] = [];
  role_options: any[] = [];

  show_view_modal = false;
  selected_log: any = null;

  columns: any[] = [
    {
      headerName: 'User ID', flex: 1.2, minWidth: 120,
      cellRenderer: (p: any) => {
        const uid = p.data?.User?.id || '—';
        return `<div style="display:flex;align-items:center;height:100%;font-size:13px;font-weight:600;color:#374151">${uid}</div>`;
      }
    },
    {
      headerName: 'Faculty Name', flex: 1.8, minWidth: 160,
      cellRenderer: (p: any) => {
        const name = p.data?.User?.name || '—';
        const role = p.data?.User?.Role?.name || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:14px;font-weight:600;color:#111827;line-height:1.3">${name}</span>
          ${role ? `<span style="font-size:12px;color:#6B7280;line-height:1.3">${role}</span>` : ''}
        </div>`;
      }
    },
    {
      headerName: 'Contact', flex: 2, minWidth: 200,
      cellRenderer: (p: any) => {
        const contact = p.data?.User?.contact ? `+91${p.data.User.contact}` : '—';
        const email = p.data?.User?.email || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:13px;font-weight:500;color:#111827;line-height:1.3">${contact}</span>
          ${email ? `<span style="font-size:12px;color:#6B7280;line-height:1.3">${email}</span>` : ''}
        </div>`;
      }
    },
    {
      headerName: 'Capture Type', flex: 1.2, minWidth: 120,
      cellRenderer: (p: any) => {
        const ct = (p.data?.capture_type || '—').toUpperCase();
        return `<div style="display:flex;align-items:center;height:100%;font-size:13px;font-weight:500;color:#374151">${ct}</div>`;
      }
    },
    {
      headerName: 'Captured Date & Time', flex: 1.8, minWidth: 170,
      valueGetter: (p: any) => {
        if (!p.data?.captured_at) return '—';
        const d = new Date(p.data.captured_at);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const h12 = d.getHours() % 12 || 12;
        const min = String(d.getMinutes()).padStart(2, '0');
        const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
        return `${dd}/${mm}/${yyyy}  ${String(h12).padStart(2, '0')}:${min}${ampm}`;
      }
    },
    {
      headerName: 'AI Trigger', flex: 1.5, minWidth: 160,
      cellRenderer: (p: any) => {
        const type = p.data?.trigger_type;
        const cfg = TRIGGER_CONFIG[type];
        if (!cfg) return `<div style="display:flex;align-items:center;height:100%;font-size:13px;color:#9CA3AF">—</div>`;
        return `<div style="display:flex;align-items:center;height:100%">
          ${WARNING_SVG(cfg.color)}
          <span style="font-size:13px;font-weight:600;color:${cfg.color}">${cfg.label}</span>
        </div>`;
      }
    },
  ];

  actions(row: any) {
    const actions: any[] = [];
    actions.push({ type: 'icon-filled', icon: 'bx bx-show', text: 'View', function: this.open_view.bind(this) });
    actions.push({ type: 'icon-filled', icon: 'bx bx-download', text: 'Download', function: this.download_image.bind(this) });
    return actions;
  }

  constructor(
    public gs: GlobalService,
    public dds: DropdownService,
    public ai: AiAdminService,
    public hs: HttpService,
  ) { }

  async ngOnInit() {
    // Access Level: lock the filters this user cannot change before the first fetch.
    this.gs.apply_access_scope_to_params(this.params);
    this.columns.push({
      headerName: 'Action', sortable: false, filter: false,
      cellRenderer: ListActionButtonsComponent,
      headerClass: 'custom-header-center',
      cellStyle: { 'justify-content': 'center' },
      cellRendererParams: { actions: this.actions.bind(this) },
      editable: false,
      pinned: 'right',
      width: 130, minWidth: 130, maxWidth: 130,
    });
    await this.dds.get_dropdown_list(['university', 'exam', 'role']);
    this.university_options = this.dds.dropdown_list?.university_list || [];
    this.exam_options = this.dds.dropdown_list?.exam_list || [];
    this.role_options = this.dds.dropdown_list?.role_list || [];
    await this.get_list();
  }

  async get_list(extra?: any) {
    try {
      const res: any = await this.ai.get_ai_logs({ ...this.params, ...extra });
      this.items = res;
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to fetch AI logs', 'Error', 'error');
    }
  }

  apply_filters() {
    this.get_list();
  }

  on_university_change() {
    this.params.exam_id = null;
    this.apply_filters();
  }

  open_view(row: any) {
    this.selected_log = row;
    this.show_view_modal = true;
  }

  close_view() {
    this.show_view_modal = false;
    this.selected_log = null;
  }

  download_image(row: any) {
    const url = row?.captured_image;
    if (!url) {
      this.gs.toastr_shows_function('No image available to download', 'Error', 'error');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-log-${row.id}.jpg`;
    a.target = '_blank';
    a.click();
  }

  format_date(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const h12 = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'pm' : 'am';
    return `${dd}/${mm}/${yyyy}  ${h12}:${min}${ampm}`;
  }

  get trigger_label(): string {
    return TRIGGER_CONFIG[this.selected_log?.trigger_type]?.label || '—';
  }

  get trigger_color(): string {
    return TRIGGER_CONFIG[this.selected_log?.trigger_type]?.color || '#9CA3AF';
  }

  get show_two_photos(): boolean {
    return this.selected_log?.trigger_type === 'face_not_matched' || this.selected_log?.trigger_type === 'login_rejected';
  }

  get show_labels_on_photos(): boolean {
    return this.selected_log?.trigger_type === 'login_rejected';
  }
}
